import { useState } from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { Modal } from "@fawkes/ui";
import {
  Cat,
  Crown,
  House,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock,
  Euro,
  FileText,
  MessageSquareText,
  PawPrint,
  Pencil,
  Sparkles,
  TriangleAlert,
  User,
  Users,
  Wrench,
} from "lucide-react";

import { formatEuro } from "../../i18n/format";
import type { PensionDier, PensionReserveringDetail, ReserveringDier, ReserveringProduct } from "../pensionKlantTypes";
import { LEEG, datumMetJaar, geslachtTeken, kortDatum, leeftijd, periode, type Context } from "./hulp";
import { nieuwConcept, verzoekVan } from "./ReserveringTab";
import "./DierVenster.css";

/**
 * Alles over één dier in een reservering, in één venster: links het plaatsingsvoorstel met het stukje planning
 * eromheen en wat het verblijf kost, rechts de reservering zelf met wat er geboekt is.
 *
 * <p>Het voorstel komt van de api ({@code .../kenneladvies}) en is gerekend uit de planning: welk hok de hele
 * periode vrij is, waar het dier de vorige keer lag, waar een huisgenoot ligt, welk accommodatietype geboekt is.
 * De redenen staan erbij, en met één klik ligt het dier erin — zonder de planning te openen.
 */

/** Zoveel dagen staan er in het plankje; met "Dag" ernaast maar één. */
const WEEK = 7;
/** Zoveel hokken staan er boven en onder het voorgestelde hok: genoeg om de buren te zien. */
const BUREN = 2;

/** Het icoontje bij een groep; het oude pakket kent geen soort, dus het gaat op de naam. */
function groepIcoon(naam: string) {
  const n = naam.toLowerCase();
  if (n.includes("quarantaine") || n.includes("geblokkeerd")) return TriangleAlert;
  if (n.includes("kat") || n.includes("poes")) return Cat;
  if (n.includes("luxe") || n.includes("suite")) return Crown;
  return House;
}

interface AdviesReden {
  code: string;
  waarde: string | null;
}

interface AdviesKandidaat {
  kennelId: number;
  kennel: string | null;
  groep: string | null;
  accommodatie: string | null;
  score: number;
  vrij: boolean;
  plaatsenVrij: number;
  capaciteit: number;
  redenen: AdviesReden[];
}

interface Advies {
  van: string;
  tot: string;
  huidigeKennelId: number | null;
  alleenInKennel: boolean;
  kandidaten: AdviesKandidaat[];
}

interface PlanKennel {
  id: number;
  naam: string | null;
  groepId: number | null;
  groep: string | null;
  diersoort: string | null;
  capaciteit: number;
}

interface PlanBoeking {
  reserveringId: number;
  dier: { id: number; naam: string | null };
  klant: { naam: string | null };
  van: string;
  tot: string;
  kennelId: number | null;
}

interface PlanBlokkade {
  id: number;
  kennelId: number;
  van: string;
  tot: string;
  soort: string;
  omschrijving: string | null;
}

interface PlanData {
  kennels: PlanKennel[];
  boekingen: PlanBoeking[];
  blokkades: PlanBlokkade[];
}

export function DierVenster({
  r,
  dier,
  eigenDier,
  foto,
  fotos,
  klantNaam,
  ctx,
  onSluit,
  onKiesDier,
  onGeplaatst,
  onOpenPlanning,
}: {
  r: PensionReserveringDetail;
  dier: ReserveringDier;
  /** Het dier van de klant zelf; daar staan de geboortedatum en de kenmerken, niet op de reserveringsregel. */
  eigenDier?: PensionDier;
  foto?: string | null;
  fotos: Record<string, string | null | undefined>;
  klantNaam?: string | null;
  ctx: Context;
  onSluit: () => void;
  onKiesDier: (id: number) => void;
  /** Na het indelen: de reservering opnieuw ophalen, want het hok staat er nu in. */
  onGeplaatst: () => void;
  /** Opent de kennelplanning; leeg als het scherm dat niet kan. */
  onOpenPlanning?: () => void;
}) {
  const { t, locale, vandaag } = ctx;
  const naam = dier.naam ?? LEEG;
  const dagen = (dier.dagenLaag ?? 0) + (dier.dagenHoog ?? 0) || r.gereserveerd.dagen;
  const producten = dier.producten ?? [];
  const teken = geslachtTeken(dier.geslacht);

  return (
    <Modal open size="wide" title={t("pensionKlant.res.dier.titel", { args: [naam] })} onClose={onSluit}>
      <div className="pk dv">
        <header className="dv__kop">
          {foto ? (
            <img className="dv__foto" src={foto} alt="" />
          ) : (
            <span className="dv__foto" aria-hidden="true">
              <PawPrint size={30} />
            </span>
          )}
          <div className="dv__wie">
            <strong>{naam}</strong>
            <span>{[dier.diersoort, dier.ras].filter(Boolean).join(" – ") || LEEG}</span>
            <span className="dv__meta">
              {teken && (
                <span className="dv__geslacht" data-g={teken === "♂" ? "m" : "v"} aria-hidden="true">
                  {teken}
                </span>
              )}
              {[dier.geslacht, leeftijd(eigenDier?.geboortedatum, ctx)].filter(Boolean).join(" • ") || LEEG}
            </span>
          </div>
          {dier.dierId != null && (
            <button type="button" className="dv__knop" onClick={() => onKiesDier(dier.dierId!)}>
              {t("pensionKlant.res.dier.naarDier")}
              <ChevronRight size={14} aria-hidden="true" />
            </button>
          )}
        </header>

        <div className="dv__kolommen">
          <div className="dv__kolom">
            <Voorstel
              r={r}
              dier={dier}
              fotos={fotos}
              klantNaam={klantNaam}
              vandaag={vandaag}
              ctx={ctx}
              onGeplaatst={onGeplaatst}
              onOpenPlanning={onOpenPlanning}
            />

            <Tarieven r={r} dier={dier} dagen={dagen} ctx={ctx} onGewijzigd={onGeplaatst} />
          </div>

          <div className="dv__kolom">
            <section className="dv__kaart" aria-label={t("pensionKlant.res.dier.reservering")}>
              <h3>
                <span className="dv__rondje" data-tint="accent" aria-hidden="true">
                  <CalendarDays size={15} />
                </span>
                {t("pensionKlant.res.dier.reservering")}
              </h3>
              <dl className="dv__gegevens">
                <Gegeven icoon={CalendarRange} label={t("pensionKlant.res.dier.periode")}>
                  {periode(r.gereserveerd.van, r.gereserveerd.tot, locale)}
                </Gegeven>
                <Gegeven icoon={Clock} label={t("pensionKlant.res.dier.dagen")}>
                  {t("pensionKlant.res.dagen", { args: [dagen] })}
                </Gegeven>
                <Gegeven icoon={CheckCircle2} label={t("pensionKlant.res.dier.status")}>
                  <span className="dv__pil" data-toon="groen">
                    {t(`pensionKlant.res.status.${r.status}`)}
                  </span>
                </Gegeven>
                <Gegeven icoon={FileText} label={t("pensionKlant.res.dier.nummer")}>
                  {r.nummer != null ? `R${r.nummer}` : LEEG}
                </Gegeven>
                <Gegeven icoon={CalendarDays} label={t("pensionKlant.res.dier.gemaaktOp")}>
                  {r.aangemaakt ? datumMetJaar(r.aangemaakt, locale) : LEEG}
                </Gegeven>
                {r.online && (
                  <Gegeven icoon={User} label={t("pensionKlant.res.dier.door")}>
                    {t("pensionKlant.res.dier.online")}
                  </Gegeven>
                )}
                {klantNaam && (
                  <Gegeven icoon={Users} label={t("pensionKlant.res.dier.klant")}>
                    {klantNaam}
                  </Gegeven>
                )}
                {r.opmerking && (
                  <Gegeven icoon={MessageSquareText} label={t("pensionKlant.res.dier.opmerkingKlant")}>
                    {r.opmerking}
                  </Gegeven>
                )}
              </dl>
            </section>

            <section className="dv__kaart" aria-label={t("pensionKlant.res.dier.geboekt")}>
              <h3>
                <span className="dv__rondje" data-tint="accent" aria-hidden="true">
                  <PawPrint size={15} />
                </span>
                {t("pensionKlant.res.dier.geboekt")}
              </h3>
              <ul className="dv__producten">
                <li>
                  <span className="dv__rondje" data-tint="grijs" aria-hidden="true">
                    <PawPrint size={14} />
                  </span>
                  <span className="dv__pnaam">
                    {t("pensionKlant.res.dier.pensionverblijf")}
                    <span>{t("pensionKlant.res.dagen", { args: [dagen] })}</span>
                  </span>
                  <span className="dv__pprijs">{dagprijs(dier, locale)}</span>
                  <span className="dv__pil" data-toon="groen">
                    {t("pensionKlant.res.dier.geboektPil")}
                  </span>
                </li>
                {producten.map((p) => (
                  <li key={p.id}>
                    <span className="dv__rondje" data-tint="grijs" aria-hidden="true">
                      <Euro size={14} />
                    </span>
                    <span className="dv__pnaam">
                      {p.naam}
                      <span>{aantalMaal(p, locale, t)}</span>
                    </span>
                    <span className="dv__pprijs">{p.prijs != null ? formatEuro(p.prijs, locale) : LEEG}</span>
                    <span className="dv__pil" data-toon="groen">
                      {t("pensionKlant.res.dier.geboektPil")}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="dv__kaart" aria-label={t("pensionKlant.res.dier.opmerkingen")}>
              <h3>
                <span className="dv__rondje" data-tint="accent" aria-hidden="true">
                  <MessageSquareText size={15} />
                </span>
                {t("pensionKlant.res.dier.opmerkingen")}
              </h3>
              <p className="dv__notitie">
                {producten.length === 0
                  ? t("pensionKlant.res.dier.geenProducten")
                  : t("pensionKlant.res.dier.verzorgingElders")}
              </p>
            </section>
          </div>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Wat het verblijf en de extra's van dit dier kosten — en aan te passen zonder eerst de hele reservering in
 * de bewerkstand te zetten: de dagprijs en de korting van dit dier, en de prijs en het aantal van zijn
 * producten. Opslaan gaat langs dezelfde PUT als het bewerken van de reservering, dus met dezelfde controles
 * en dezelfde versiecontrole.
 */
function Tarieven({
  r,
  dier,
  dagen,
  ctx,
  onGewijzigd,
}: {
  r: PensionReserveringDetail;
  dier: ReserveringDier;
  dagen: number;
  ctx: Context;
  onGewijzigd: () => void;
}) {
  const { t, locale } = ctx;
  const [bewerk, setBewerk] = useState(false);
  const [prijs, setPrijs] = useState(getal(dier.prijsLaag));
  const [korting, setKorting] = useState(getal(dier.korting));
  const [regels, setRegels] = useState<Record<number, { prijs: string; aantal: string }>>({});
  const [fout, setFout] = useState<string | null>(null);
  const { mutateAsync, isLoading: bezig } = useCustomMutation();
  const producten = dier.producten ?? [];

  const begin = () => {
    setPrijs(getal(dier.prijsLaag));
    setKorting(getal(dier.korting));
    setRegels(Object.fromEntries(producten.map((p) => [p.id, { prijs: getal(p.prijs), aantal: getal(p.aantal) }])));
    setFout(null);
    setBewerk(true);
  };

  // Wat er nu op het scherm staat, zodat de bedragen meelopen terwijl je typt.
  const nu = bewerk
    ? {
        verblijf: nummer(prijs) * dagen - nummer(korting),
        producten: producten.map((p) => ({
          ...p,
          prijs: nummer(regels[p.id]?.prijs ?? getal(p.prijs)),
          aantal: nummer(regels[p.id]?.aantal ?? getal(p.aantal)),
          totaal: nummer(regels[p.id]?.prijs ?? getal(p.prijs)) * nummer(regels[p.id]?.aantal ?? getal(p.aantal)) - (p.korting ?? 0),
        })),
      }
    : { verblijf: dier.verblijfBedrag, producten };
  const som = nu.verblijf + nu.producten.reduce((t2, p) => t2 + p.totaal, 0);

  const bewaar = async () => {
    setFout(null);
    const concept = nieuwConcept(r);
    concept.dieren[dier.regelId] = { ...concept.dieren[dier.regelId], prijs, korting };
    for (const p of producten) {
      const sleutel = `DIER:${p.id}`;
      const x = regels[p.id];
      if (x && concept.producten[sleutel]) {
        concept.producten[sleutel] = { ...concept.producten[sleutel], prijs: x.prijs, aantal: x.aantal };
      }
    }
    try {
      await mutateAsync({
        url: `pension/reserveringen/${r.id}`,
        method: "put",
        values: verzoekVan(r, concept),
        successNotification: false,
        errorNotification: false,
      });
      setBewerk(false);
      onGewijzigd();
    } catch (e) {
      setFout((e as { statusCode?: number } | null)?.statusCode === 409 ? "conflict" : "mislukt");
    }
  };

  return (
    <section className="dv__kaart" aria-label={t("pensionKlant.res.dier.tarieven")}>
      <h3>
        <span className="dv__rondje" data-tint="accent" aria-hidden="true">
          <ClipboardList size={15} />
        </span>
        {t("pensionKlant.res.dier.tarieven")}
        {!bewerk && (
          <button type="button" className="dv__knop dv__knop--klein dv__kaartknop" onClick={begin}>
            <Pencil size={12} aria-hidden="true" />
            {t("pensionKlant.res.dier.tarievenAanpassen")}
          </button>
        )}
      </h3>
      <dl className="dv__prijzen">
        <div>
          <dt>
            {t("pensionKlant.res.dier.pensionverblijf")}
            {bewerk ? (
              <span className="dv__invoerrij">
                <label>
                  {t("pensionKlant.res.dier.dagprijs")}
                  <input inputMode="decimal" value={prijs} onChange={(e) => setPrijs(e.target.value)} />
                </label>
                <label>
                  {t("pensionKlant.res.dier.kortingVeld")}
                  <input inputMode="decimal" value={korting} onChange={(e) => setKorting(e.target.value)} />
                </label>
                <span>{t("pensionKlant.res.dagen", { args: [dagen] })}</span>
              </span>
            ) : (
              <span>{t("pensionKlant.res.dier.dagenMaal", { args: [dagen, dagprijs(dier, locale)] })}</span>
            )}
          </dt>
          <dd>{formatEuro(nu.verblijf, locale)}</dd>
        </div>
        {nu.producten.map((p) => (
          <div key={p.id}>
            <dt>
              {p.naam}
              {bewerk ? (
                <span className="dv__invoerrij">
                  <label>
                    {t("pensionKlant.res.dier.stukprijs")}
                    <input
                      inputMode="decimal"
                      value={regels[p.id]?.prijs ?? ""}
                      onChange={(e) =>
                        setRegels((x) => ({ ...x, [p.id]: { ...x[p.id], prijs: e.target.value } }))
                      }
                    />
                  </label>
                  <label>
                    {t("pensionKlant.res.dier.aantal")}
                    <input
                      inputMode="numeric"
                      value={regels[p.id]?.aantal ?? ""}
                      onChange={(e) =>
                        setRegels((x) => ({ ...x, [p.id]: { ...x[p.id], aantal: e.target.value } }))
                      }
                    />
                  </label>
                </span>
              ) : (
                <span>{aantalMaal(p, locale, t)}</span>
              )}
            </dt>
            <dd>{formatEuro(p.totaal, locale)}</dd>
          </div>
        ))}
        <div className="dv__totaal">
          <dt>{t("pensionKlant.res.dier.totaal")}</dt>
          <dd>{formatEuro(som, locale)}</dd>
        </div>
      </dl>
      {fout && (
        <p className="dv__fout" role="alert">
          {t(fout === "conflict" ? "pensionKlant.res.bewerk.conflict" : "pensionKlant.res.bewerk.mislukt")}
        </p>
      )}
      {bewerk && (
        <div className="dv__kaartvoet">
          <button type="button" className="dv__knop dv__knop--klein" disabled={bezig} onClick={() => setBewerk(false)}>
            {t("common.cancel")}
          </button>
          <button type="button" className="dv__knop dv__knop--klein dv__knop--hoofd" disabled={bezig} onClick={() => void bewaar()}>
            {t("common.save")}
          </button>
        </div>
      )}
    </section>
  );
}

/** Een bedrag als tekst om in te typen: 12.5 → "12,5". */
function getal(waarde: number | null | undefined): string {
  return waarde == null ? "" : String(waarde).replace(".", ",");
}

function nummer(tekst: string): number {
  const n = Number(tekst.replace(",", ".").trim());
  return Number.isFinite(n) ? n : 0;
}

function Gegeven({
  icoon: Icoon,
  label,
  children,
}: {
  icoon: typeof CalendarDays;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="dv__gegeven">
      <dt>
        <Icoon size={14} aria-hidden="true" />
        {label}
      </dt>
      <dd>{children}</dd>
    </div>
  );
}

/** Het plaatsingsvoorstel met het plankje eromheen. */
function Voorstel({
  r,
  dier,
  fotos,
  klantNaam,
  vandaag,
  ctx,
  onGeplaatst,
  onOpenPlanning,
}: {
  r: PensionReserveringDetail;
  dier: ReserveringDier;
  fotos: Record<string, string | null | undefined>;
  klantNaam?: string | null;
  vandaag: string;
  ctx: Context;
  onGeplaatst: () => void;
  onOpenPlanning?: () => void;
}) {
  const { t, locale } = ctx;
  const start = (r.gereserveerd.van ?? vandaag).slice(0, 10);
  const [begin, setBegin] = useState(start);
  const [breed, setBreed] = useState<number>(WEEK);
  const [fout, setFout] = useState(false);
  const { mutateAsync, isLoading: bezig } = useCustomMutation();

  const eind = schuif(begin, breed - 1);
  const advies = useCustom<Advies>({
    url: `pension/reserveringen/${r.id}/dieren/${dier.dierId}/kenneladvies`,
    method: "get",
    queryOptions: { enabled: dier.dierId != null, retry: false },
  });
  const plan = useCustom<PlanData>({
    url: `pension/planning?van=${begin}&tot=${eind}`,
    method: "get",
    queryOptions: { retry: false },
  });
  const beste = advies.data?.data?.kandidaten?.[0];
  const dagen = dagenVan(begin, eind);

  const plaats = async (kennelId: number) => {
    setFout(false);
    try {
      await mutateAsync({
        url: "pension/planning/plaatsingen",
        method: "put",
        values: { reserveringId: r.id, dierId: dier.dierId, kennelId },
        successNotification: false,
        errorNotification: false,
      });
      onGeplaatst();
      void advies.refetch();
      void plan.refetch();
    } catch {
      setFout(true);
    }
  };

  return (
    <section className="dv__kaart" aria-label={t("pensionKlant.res.dier.voorstel")}>
      <h3>
        <span className="dv__rondje" data-tint="accent" aria-hidden="true">
          <Sparkles size={15} />
        </span>
        {t("pensionKlant.res.dier.voorstel")}
        <span className="dv__pil dv__pil--ai">{t("pensionKlant.res.dier.aiAdvies")}</span>
      </h3>

      {beste ? (
        <p className="dv__tip">
          <CheckCircle2 size={16} aria-hidden="true" />
          <span>
            <strong>{t("pensionKlant.res.dier.advies.voorstelKop", { args: [beste.kennel ?? LEEG] })}</strong>
            {beste.redenen.map((x) => redenTekst(x, ctx)).filter(Boolean).join(" · ")}
          </span>
          {advies.data?.data?.huidigeKennelId !== beste.kennelId && (
            <button type="button" className="dv__knop dv__knop--hoofd" disabled={bezig} onClick={() => void plaats(beste.kennelId)}>
              {t("pensionKlant.res.dier.advies.plaats")}
            </button>
          )}
        </p>
      ) : (
        <p className="dv__tip" data-leeg="true">
          {advies.isLoading ? t("common.loading") : t("pensionKlant.res.dier.advies.geen")}
        </p>
      )}

      <div className="dv__balk">
        <button type="button" className="dv__knop dv__knop--klein" onClick={() => setBegin(start)}>
          {t("pensionKlant.res.dier.vandaag")}
        </button>
        <span className="dv__stapper">
          <button type="button" aria-label={t("pensionKlant.res.dier.eerder")} onClick={() => setBegin(schuif(begin, -breed))}>
            <ChevronLeft size={14} aria-hidden="true" />
          </button>
          <button type="button" aria-label={t("pensionKlant.res.dier.later")} onClick={() => setBegin(schuif(begin, breed))}>
            <ChevronRight size={14} aria-hidden="true" />
          </button>
        </span>
        <span className="dv__periode">
          <CalendarDays size={13} aria-hidden="true" />
          {breed === 1 ? weekdagDatum(begin, locale) : `${weekdagDatum(begin, locale)} – ${weekdagDatum(eind, locale)}`}
        </span>
        <span className="dv__schakel">
          <button type="button" aria-pressed={breed === 1} onClick={() => setBreed(1)}>
            {t("pensionKlant.res.dier.dag")}
          </button>
          <button type="button" aria-pressed={breed === WEEK} onClick={() => setBreed(WEEK)}>
            {t("pensionKlant.res.dier.week")}
          </button>
        </span>
      </div>

      <Plankje
        data={plan.data?.data}
        dagen={dagen}
        vandaag={vandaag}
        dier={dier}
        reserveringId={r.id}
        voorstelKennelId={beste?.kennelId ?? null}
        huidigeKennelId={advies.data?.data?.huidigeKennelId ?? null}
        ankerKennelId={advies.data?.data?.huidigeKennelId ?? beste?.kennelId ?? null}
        van={(r.gereserveerd.van ?? begin).slice(0, 10)}
        tot={(r.gereserveerd.tot ?? eind).slice(0, 10)}
        fotos={fotos}
        klantNaam={klantNaam}
        bezig={bezig}
        ctx={ctx}
        onPlaats={plaats}
      />

      {fout && (
        <p className="dv__fout" role="alert">
          {t("pensionKlant.res.dier.advies.fout")}
        </p>
      )}
      {onOpenPlanning && (
        <button type="button" className="dv__planningknop" onClick={onOpenPlanning}>
          <CalendarDays size={15} aria-hidden="true" />
          {t("pensionKlant.res.dier.openPlanning")}
        </button>
      )}
    </section>
  );
}

/** Het stukje planning: de hokken per groep, met wie er die dagen ligt en waar dit dier zou komen. */
export function Plankje({
  data,
  dagen,
  vandaag,
  dier,
  reserveringId,
  voorstelKennelId,
  huidigeKennelId,
  ankerKennelId,
  van,
  tot,
  fotos,
  klantNaam,
  bezig,
  ctx,
  onPlaats,
}: {
  data?: PlanData;
  dagen: string[];
  vandaag: string;
  dier: ReserveringDier;
  reserveringId: number;
  voorstelKennelId: number | null;
  /** Waar het dier nu ligt; leeg als het nog nergens ligt. */
  huidigeKennelId: number | null;
  /** Het hok waar het plankje omheen wordt gesneden: waar het dier ligt, of waar het zou komen. */
  ankerKennelId: number | null;
  van: string;
  tot: string;
  fotos: Record<string, string | null | undefined>;
  klantNaam?: string | null;
  bezig: boolean;
  ctx: Context;
  onPlaats: (kennelId: number) => void;
}) {
  const { t, locale } = ctx;
  const [alles, setAlles] = useState(false);
  // Defensief: een antwoord zonder lijsten (of een api die even niets teruggeeft) mag het venster niet slopen.
  const alleKennels = Array.isArray(data?.kennels) ? data.kennels : [];
  const boekingen = Array.isArray(data?.boekingen) ? data.boekingen : [];
  const blokkades = Array.isArray(data?.blokkades) ? data.blokkades : [];
  if (!data) return <p className="dv__zacht">{t("common.loading")}</p>;

  // Een vestiging heeft tientallen hokken; je wilt er één zien met zijn buren. Zonder anker de eerste paar.
  const kennels = alles ? alleKennels : buurt(alleKennels, ankerKennelId);
  const verborgen = alleKennels.length - kennels.length;
  const eigenFoto = dier.dierId == null ? null : fotos[String(dier.dierId)];

  const groepen = new Map<string, PlanKennel[]>();
  for (const k of kennels) {
    const naam = k.groep ?? t("pensionKlant.res.dier.zonderGroep");
    groepen.set(naam, [...(groepen.get(naam) ?? []), k]);
  }
  const kolommen = `minmax(7rem, 1fr) repeat(${dagen.length}, minmax(0, 1fr))`;
  const kolom = (datum: string) => dagen.indexOf(datum);

  /** Waar een balk begint en eindigt in het raster; buiten beeld wordt afgekapt. */
  const strook = (bVan: string, bTot: string) => {
    const eerste = Math.max(0, kolom(bVan) < 0 ? (bVan < dagen[0] ? 0 : -1) : kolom(bVan));
    const laatste = kolom(bTot) < 0 ? (bTot > dagen[dagen.length - 1] ? dagen.length - 1 : -1) : kolom(bTot);
    if (eerste < 0 || laatste < 0 || laatste < eerste) return null;
    return { gridColumn: `${eerste + 2} / ${laatste + 3}` };
  };

  return (
    <div className="dv__plankje" role="table" aria-label={t("pensionKlant.res.dier.plankjeAria")}>
      <div className="dv__rij dv__rij--kop" style={{ gridTemplateColumns: kolommen }} role="row">
        <span />
        {dagen.map((d) => (
          <span key={d} className="dv__dagkop" data-vandaag={d === vandaag || undefined} role="columnheader">
            <span>{weekdagKort(d, locale)}</span>
            <strong>{kortDatum(d, locale)}</strong>
          </span>
        ))}
      </div>

      {[...groepen.entries()].map(([groep, kennels]) => (
        <div key={groep} className="dv__groep">
          <p className="dv__groepnaam">
            {(() => {
              const Icoon = groepIcoon(groep);
              return <Icoon size={13} aria-hidden="true" />;
            })()}
            {groep}
          </p>
          {kennels.map((k) => {
            const erin = boekingen.filter((b) => b.kennelId === k.id);
            const blok = blokkades.filter((b) => b.kennelId === k.id);
            const bezet = erin.filter((b) => b.van <= tot && b.tot >= van).length;
            const voorstel = k.id === voorstelKennelId;
            return (
              <div key={k.id} className="dv__rij" style={{ gridTemplateColumns: kolommen }} role="row">
                <span className="dv__hok" data-voorstel={voorstel || undefined} title={k.naam ?? undefined}>
                  <span className="dv__hoknaam">{hokNaamInGroep(k.naam, groep) ?? LEEG}</span>
                  <span className="dv__hokpunt" aria-hidden="true">•</span>
                  <span className="dv__hoktelling">
                    {bezet}/{k.capaciteit}
                  </span>
                </span>
                {/* Elk vakje vast op zijn eigen dag. Zonder vaste plek schuift het raster de vakjes door naar een
                    tweede regel zodra er een balkje over die dagen ligt (Maarten, 2026-10-14: "tekent niet meer goed"). */}
                {dagen.map((d, i) => (
                  <span key={d} className="dv__vak" style={{ gridColumn: i + 2 }} data-vandaag={d === vandaag || undefined} />
                ))}
                {blok.map((b) => {
                  const plek = strook(b.van, b.tot);
                  return plek == null ? null : (
                    <span key={`b${b.id}`} className="dv__balkje dv__balkje--blok" style={plek} title={b.omschrijving ?? undefined}>
                      <Wrench size={11} aria-hidden="true" />
                      {t(`pensionKlant.res.dier.blokkade.${b.soort}`)}
                    </span>
                  );
                })}
                {erin.map((b) => {
                  const plek = strook(b.van, b.tot);
                  if (plek == null) return null;
                  const foto = fotos[String(b.dier.id)];
                  const eigen = b.reserveringId === reserveringId;
                  return (
                    <span
                      key={`d${b.dier.id}-${b.van}`}
                      className="dv__balkje"
                      data-eigen={eigen || undefined}
                      style={plek}
                      title={`${b.dier.naam ?? "?"} – ${b.klant.naam ?? ""}`}
                    >
                      {foto ? <img src={foto} alt="" /> : <PawPrint size={11} aria-hidden="true" />}
                      {b.dier.naam ?? LEEG}
                      {b.klant.naam ? ` - ${achternaam(b.klant.naam)}` : ""}
                    </span>
                  );
                })}
                {/* Op elk hok waar dit dier past een knop, en niet alleen op het voorstel. Het advies is
                    een advies: wie weet dat Beertje beter naast Puk ligt moet dat hier kunnen doen in
                    plaats van eerst naar de planning te moeten. Alleen niet op het hok waar hij al ligt. */}
                {k.id !== huidigeKennelId && strook(van, tot) && (
                  <button
                    type="button"
                    className="dv__balkje dv__balkje--voorstel"
                    data-advies={voorstel || undefined}
                    style={strook(van, tot) ?? undefined}
                    disabled={bezig}
                    title={t(huidigeKennelId == null
                      ? "pensionKlant.res.dier.hierPlaatsen"
                      : "pensionKlant.res.dier.hierheenVerplaatsen", { args: [k.naam ?? ""] })}
                    aria-label={t(huidigeKennelId == null
                      ? "pensionKlant.res.dier.hierPlaatsen"
                      : "pensionKlant.res.dier.hierheenVerplaatsen", { args: [k.naam ?? ""] })}
                    onClick={() => onPlaats(k.id)}
                  >
                    {voorstel && eigenFoto ? <img src={eigenFoto} alt="" /> : <Sparkles size={11} aria-hidden="true" />}
                    <span>
                      {voorstel
                        ? `${dier.naam ?? LEEG}${klantNaam ? " - " + achternaam(klantNaam) : ""}`
                        : t(huidigeKennelId == null
                          ? "pensionKlant.res.dier.hierKort"
                          : "pensionKlant.res.dier.hierheenKort")}
                    </span>
                    <ChevronRight size={11} aria-hidden="true" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ))}
      {kennels.length === 0 && (
        <p className="dv__zacht">
          <TriangleAlert size={14} aria-hidden="true" /> {t("pensionKlant.res.dier.geenHokken")}
        </p>
      )}
      {(verborgen > 0 || alles) && (
        <button type="button" className="dv__meer" onClick={() => setAlles((x) => !x)}>
          {alles
            ? t("pensionKlant.res.dier.minderHokken")
            : t("pensionKlant.res.dier.meerHokken", { args: [verborgen] })}
        </button>
      )}
    </div>
  );
}

/**
 * Het hok waar het om gaat met zijn buren: twee erboven en twee eronder, zodat het dier in het midden staat.
 * Ligt het hok aan het begin of het eind van een groep, dan komen de buren uit de groep ernaast — de hokken
 * staan in de planning toch al in de volgorde waarin ze in huis staan.
 */
function buurt(alle: PlanKennel[], ankerId: number | null): PlanKennel[] {
  if (alle.length === 0) return alle;
  const anker = alle.find((k) => k.id === ankerId);
  if (!anker) return alle.slice(0, BUREN * 2 + 1);
  // De buren komen uit de eigen groep: een pensionhok aan de rand hoort niet naast de dagopvang te staan.
  const kennels = alle.filter((k) => k.groep === anker.groep);
  const plek = kennels.indexOf(anker);
  const eerste = Math.max(0, Math.min(plek - BUREN, kennels.length - (BUREN * 2 + 1)));
  return kennels.slice(eerste, eerste + BUREN * 2 + 1);
}

// ── Rekenwerk en tekst ───────────────────────────────────────────────────────────────────────────────

function dagprijs(dier: ReserveringDier, locale: string): string {
  const prijs = dier.prijsLaag ?? dier.prijsHoog;
  return prijs == null ? LEEG : formatEuro(prijs, locale);
}

function aantalMaal(p: ReserveringProduct, locale: string, t: Context["t"]): string {
  const stuk = p.prijs == null ? LEEG : formatEuro(p.prijs, locale);
  return p.aantal == null || p.aantal === 1 ? stuk : t("pensionKlant.res.dier.dagenMaal", { args: [p.aantal, stuk] });
}

/**
 * De naam van een hok zonder de groepsnaam die er al boven staat: onder "Pension gang 1 (grote kennel 4 tm 6)" is
 * "Pension gang 1 (grote kennel 4 tm 6) - 5" gewoon "5". Anders zijn afgekapte namen niet van elkaar te onderscheiden.
 */
export function hokNaamInGroep(naam: string | null, groep: string): string | null {
  if (!naam) return naam;
  if (!groep || !naam.toLowerCase().startsWith(groep.toLowerCase())) return naam;
  const rest = naam.slice(groep.length).replace(/^[\s\-–—:·]+/, "").trim();
  return rest || naam;
}

export function redenTekst(reden: AdviesReden, ctx: Context): string {
  return ctx.t(`pensionKlant.res.dier.advies.reden.${reden.code}`, reden.waarde ? { args: [reden.waarde] } : undefined);
}

/** "van Dijk" uit "Familie van Dijk": op het balkje past alleen de naam zelf. */
function achternaam(naam: string): string {
  return naam.replace(/^(fam(ilie)?\.?|dhr\.?|mevr\.?|mw\.?)\s+/i, "").trim();
}

function dagenVan(van: string, tot: string): string[] {
  const uit: string[] = [];
  for (let d = new Date(`${van}T00:00:00Z`); d <= new Date(`${tot}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1)) {
    uit.push(d.toISOString().slice(0, 10));
  }
  return uit;
}

function schuif(iso: string, dagen: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dagen);
  return d.toISOString().slice(0, 10);
}

function weekdagKort(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" })
    .format(new Date(`${iso}T00:00:00Z`))
    .replace(".", "");
}

function weekdagDatum(iso: string, locale: string): string {
  return `${weekdagKort(iso, locale)} ${kortDatum(iso, locale)}`;
}
